// **A confetti burst, hand-rolled** (`player-chat-and-outcome` **D-6**; the same module the
// match-HUD plan's "Red wins" banner wants, specified identically in both so the two cannot
// diverge).
//
// It follows `canvas-confetti`'s visual model without the dependency: **paper rectangles** rather
// than dots, launched with **randomised spread and initial velocity**, carried by **gravity plus
// drag**, each one **tumbling** about its own axis, and **fading out as it decays**. The tumble is
// what sells paper — a rectangle whose height is scaled by `cos(tilt)` reads as a flat card turning
// edge-on and back, which no amount of rotation alone achieves.
//
// **Self-contained on purpose.** No imports at all: not from screen code, not from the renderer, not
// from a package. Two worktrees were writing this file at once, so the smaller and more obvious it
// is, the less there is to reconcile. It also means the module can be dropped in anywhere a DOM
// element exists — it brings its own `<canvas>`, sizes it to the host, and takes it away again.
//
// **Not PixiJS.** The plan noted that `pixi.js` is already the renderer and is "the only thing
// needed" — read as *no new dependency*, which this honours. A WebGL application for fifty paper
// rectangles over a DOM banner would cost a context, a ticker and a teardown path that jsdom cannot
// construct, so the lifecycle this module actually has to get right (see `confetti.test.ts`) would
// become the part that is untestable. A 2D context is the native platform feature for this.
//
// **`prefers-reduced-motion` is a hard gate**, not a slowdown: no canvas is mounted, no frame is
// requested, and no particle exists. The banner alone is the celebration in that case.

/** How the burst is shaped. Every field has a working default; a caller that just wants confetti
 * passes nothing. */
export interface ConfettiOptions {
  /** How many paper rectangles to launch. */
  particleCount?: number;
  /** Launch point as a fraction of the host box — `{ x: 0.5, y: 0.4 }` by default, a little above
   * centre so the fall is visible rather than immediately off the bottom edge. */
  origin?: { x: number; y: number };
  /** The arc particles are launched into, in degrees, centred on straight up. */
  spreadDeg?: number;
  /** How long a particle lives before it has fully faded, in milliseconds. */
  ttlMs?: number;
  /** The paper's colours, sampled per particle. */
  colors?: readonly string[];
}

export interface ConfettiHandle {
  /** Retires every particle, cancels the frame loop and removes the canvas. Idempotent — a banner
   * that dismisses itself and then unmounts will call this twice. */
  stop(): void;
  /** Particles still in flight. `0` before launch under reduced motion, and `0` once the burst has
   * finished or been stopped — which is what makes "no orphaned ticker" assertable. */
  readonly particleCount: number;
}

/** The kit's honey/accent family, so a win reads as this game's win. Literals rather than CSS
 * variables: a canvas cannot resolve `var(--honey-400)`, and this module deliberately reaches for
 * nothing outside itself. */
const DEFAULT_COLORS = ['#F2B705', '#E8A33D', '#FFD966', '#C6862B', '#FFF3CB'] as const;

const GRAVITY_PX_PER_MS2 = 0.0016;
/** Velocity retained per millisecond — air resistance, so the arc flattens rather than tracing a
 * clean parabola. */
const DRAG_PER_MS = 0.998;
const PAPER_WIDTH_PX = 9;
const PAPER_HEIGHT_PX = 5;
/** Frames are clamped to this before integrating, so a backgrounded tab that resumes after a long
 * gap does not teleport every particle off-screen in one step. */
const MAX_STEP_MS = 50;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Radians turned about the paper's own face — the in-plane spin. */
  spin: number;
  spinRate: number;
  /** Radians of the edge-on tumble; drives the height squash that makes it read as paper. */
  tilt: number;
  tiltRate: number;
  ageMs: number;
  ttlMs: number;
  color: string;
}

/** True only when the display has actually said it wants less motion. A display that cannot report
 * a preference has not expressed one — the same precedence `SandboxScreen`'s growth transition and
 * `BusyBee`'s flap already use, matched deliberately so every moving thing in this client answers
 * the question the same way. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** jsdom implements no 2D context (and a browser can refuse one under memory pressure), so this is
 * allowed to answer `null` — the burst then runs its lifecycle and simply draws nothing, which is
 * exactly what the tests exercise. */
function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return canvas.getContext('2d');
  } catch {
    return null;
  }
}

export function launchConfetti(container: HTMLElement, options: ConfettiOptions = {}): ConfettiHandle {
  const count = options.particleCount ?? 90;
  const spread = ((options.spreadDeg ?? 110) * Math.PI) / 180;
  const ttl = options.ttlMs ?? 2600;
  const colors = options.colors ?? DEFAULT_COLORS;
  const originX = options.origin?.x ?? 0.5;
  const originY = options.origin?.y ?? 0.4;

  let particles: Particle[] = [];
  let frame: number | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let lastMs: number | null = null;

  function teardown(): void {
    if (frame !== null) {
      window.cancelAnimationFrame(frame);
      frame = null;
    }
    canvas?.remove();
    canvas = null;
    ctx = null;
    particles = [];
  }

  const handle: ConfettiHandle = {
    stop: teardown,
    get particleCount() {
      return particles.length;
    },
  };

  if (prefersReducedMotion()) {
    return handle; // no canvas, no frame, no particles — the banner alone
  }

  // `clientWidth`/`clientHeight` are 0 in jsdom; the fallback keeps the launch arithmetic finite so
  // the particle model is still exercised under test.
  const width = container.clientWidth || 360;
  const height = container.clientHeight || 240;

  canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.position = 'absolute';
  canvas.style.inset = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.pointerEvents = 'none';
  container.appendChild(canvas);
  ctx = context2d(canvas);

  for (let i = 0; i < count; i += 1) {
    // Straight up (-π/2) plus a random offset within the spread arc, and a randomised speed, so the
    // burst has a shape rather than a uniform fan.
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread;
    const speed = 0.25 + Math.random() * 0.45;
    particles.push({
      x: width * originX,
      y: height * originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      spin: Math.random() * Math.PI * 2,
      spinRate: (Math.random() - 0.5) * 0.012,
      tilt: Math.random() * Math.PI * 2,
      tiltRate: 0.004 + Math.random() * 0.008,
      ageMs: 0,
      // Staggered lifetimes, so the burst thins out instead of vanishing all at once.
      ttlMs: ttl * (0.6 + Math.random() * 0.4),
      color: colors[Math.floor(Math.random() * colors.length)] ?? DEFAULT_COLORS[0],
    });
  }

  function step(nowMs: number): void {
    frame = null;
    const deltaMs = lastMs === null ? 16 : Math.min(nowMs - lastMs, MAX_STEP_MS);
    lastMs = nowMs;

    const alive: Particle[] = [];
    for (const p of particles) {
      p.ageMs += deltaMs;
      if (p.ageMs >= p.ttlMs) {
        continue;
      }
      const drag = Math.pow(DRAG_PER_MS, deltaMs);
      p.vx *= drag;
      p.vy = p.vy * drag + GRAVITY_PX_PER_MS2 * deltaMs;
      p.x += p.vx * deltaMs;
      p.y += p.vy * deltaMs;
      p.spin += p.spinRate * deltaMs;
      p.tilt += p.tiltRate * deltaMs;
      alive.push(p);
    }
    particles = alive;

    if (ctx !== null && canvas !== null) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.save();
        ctx.globalAlpha = 1 - p.ageMs / p.ttlMs; // fade out on decay
        ctx.translate(p.x, p.y);
        ctx.rotate(p.spin);
        // The tumble: a flat card turning edge-on has zero apparent height, so squashing by
        // `cos(tilt)` is the whole trick. `PAPER_*` are the card's face dimensions.
        ctx.scale(1, Math.cos(p.tilt));
        ctx.fillStyle = p.color;
        ctx.fillRect(-PAPER_WIDTH_PX / 2, -PAPER_HEIGHT_PX / 2, PAPER_WIDTH_PX, PAPER_HEIGHT_PX);
        ctx.restore();
      }
    }

    if (particles.length === 0) {
      teardown(); // the burst retires itself — nothing has to remember to call stop()
      return;
    }
    frame = window.requestAnimationFrame(step);
  }

  frame = window.requestAnimationFrame(step);
  return handle;
}
